import { ChangeDetectionStrategy,Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PwaInstallationService } from '@app/services/pwa-installation/pwa-installation.service';

export type InstallationType = 'ios' | 'chrome';

@Component({
  selector: 'app-pwa-popup',
  templateUrl: './pwa-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pwa-popup.component.scss']
})
export class PwaPopupComponent implements OnInit {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly pwaInstallationService: PwaInstallationService = inject(PwaInstallationService);
  public isVisible = true;
  public installationType: InstallationType = 'ios';

  public async ngOnInit(): Promise<void> {
    // Check if running in standalone mode first
    if (this.pwaInstallationService.isRunningInStandaloneMode()) {
      await this.pwaInstallationService.skipInstallation();
      this.isVisible = false;
      return;
    }

    // Subscribe to installation availability changes
    this.pwaInstallationService.installationAvailable$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async available => {
        if (available) {
          await this.checkAndShowInstallation();
        }
      });

    // Initial check
    await this.checkAndShowInstallation();
  }

  private async checkAndShowInstallation(): Promise<void> {
    // Check for Chrome installation first
    if (await this.pwaInstallationService.shouldShowChromeInstallation()) {
      this.installationType = 'chrome';
      this.isVisible = true;
      return;
    }

    // Check for iOS installation
    if (await this.pwaInstallationService.shouldShowIosInstallation()) {
      this.installationType = 'ios';
      this.isVisible = true;
      return;
    }

    this.isVisible = false;
  }

  public async onClose(): Promise<void> {
    this.isVisible = false;
    await this.pwaInstallationService.skipInstallation();
  }

  public async onInstall(): Promise<void> {
    if (this.installationType === 'chrome') {
      const success = await this.pwaInstallationService.triggerChromeInstallation();
      if (success) {
        this.isVisible = false;
      }
    }
    // For iOS, the user needs to follow the manual instructions shown in the popup
  }
}
